import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

const TOOLTIP_CLASS =
  "pointer-events-none whitespace-nowrap rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-text-primary shadow-lg transition-opacity duration-150";

/**
 * A small label shown above a toolbar button on hover or focus.
 *
 * `portal` draws it on the page body, fixed above the button, for buttons
 * inside a container that clips its overflow (Scout's panel header): drawn in
 * place, the label would be cut off at the container's edge.
 */
export function ChatToolbarTooltip({
  label,
  children,
  className,
  portal = false,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  portal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  const show = () => {
    if (portal && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setAnchor({ x: r.left + r.width / 2, y: r.top });
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <span
      ref={ref}
      className={cn("relative inline-flex", className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onPointerDownCapture={hide}
      onClickCapture={hide}
    >
      {children}
      {portal ? (
        open && anchor &&
        createPortal(
          <span
            className={cn(TOOLTIP_CLASS, "z-[80]")}
            style={{
              position: "fixed",
              left: anchor.x,
              top: anchor.y - 8,
              transform: "translate(-50%, -100%)",
            }}
          >
            {label}
          </span>,
          document.body,
        )
      ) : (
        <span
          className={cn(
            TOOLTIP_CLASS,
            "absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2",
            open ? "opacity-100" : "opacity-0",
          )}
        >
          {label}
        </span>
      )}
    </span>
  );
}
